import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

import { PaymentService, PaymentType, PAYMENT_CATEGORIES, PaymentCategory } from '../../../services/payment';
import { ToastService } from '../../../services/toast';
import { PageTitleService } from '../../../services/page-title';

@Component({
  selector: 'app-payment-types',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    SelectModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    InputNumberModule,
    ToggleSwitchModule,
  ],
  templateUrl: './payment-types.html',
  styleUrls: ['./payment-types.css'],
})
export class PaymentTypesPage implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(PaymentService);
  private toast = inject(ToastService);
  readonly pageTitle = inject(PageTitleService).title;
  private platformId = inject(PLATFORM_ID);

  readonly categories: PaymentCategory[] = [...PAYMENT_CATEGORIES];
  readonly categoryLabels: Record<string, string> = {
    PAYMENT_STATUS: 'Payment Status',
    PAYMENT_MODE: 'Payment Mode',
    PAYER_TYPE: 'Payer Type',
  };

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  activeCategory = signal<PaymentCategory>('PAYMENT_STATUS');
  rows = signal<PaymentType[]>([]);

  /** Rows for the currently selected category tab, sorted by display order. */
  visible = computed(() =>
    this.rows()
      .filter((r) => r.category === this.activeCategory())
      .sort((a, b) => a.displayOrder - b.displayOrder),
  );

  form: FormGroup = this.fb.group({
    id: [0],
    category: ['PAYMENT_STATUS' as PaymentCategory, Validators.required],
    typeCode: ['', Validators.required],
    typeName: ['', Validators.required],
    displayOrder: [0],
    isActive: [true],
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.load();
  }

  load() {
    this.loading.set(true);
    this.svc.ptGetAll().subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.rows.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  selectCategory(c: PaymentCategory) {
    this.activeCategory.set(c);
  }

  countOf(c: PaymentCategory): number {
    return this.rows().filter((r) => r.category === c).length;
  }

  openAdd() {
    this.editing.set(false);
    const nextOrder = (this.visible().at(-1)?.displayOrder ?? 0) + 1;
    this.form.reset({
      id: 0,
      category: this.activeCategory(),
      typeCode: '',
      typeName: '',
      displayOrder: nextOrder,
      isActive: true,
    });
    this.showModal.set(true);
  }

  openEdit(r: PaymentType) {
    this.editing.set(true);
    this.form.reset({
      id: r.id,
      category: r.category,
      typeCode: r.typeCode ?? '',
      typeName: r.typeName ?? '',
      displayOrder: r.displayOrder ?? 0,
      isActive: r.isActive ?? true,
    });
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
  }

  onCodeInput() {
    const ctrl = this.form.get('typeCode');
    const up = (ctrl?.value ?? '').toString().toUpperCase();
    if (up !== ctrl?.value) ctrl?.setValue(up, { emitEvent: false });
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      if (this.form.get('typeCode')?.invalid) this.toast.warning('Type code is required.');
      else if (this.form.get('typeName')?.invalid) this.toast.warning('Type name is required.');
      else this.toast.warning('Please complete the required fields.');
      return;
    }
    const v = this.form.getRawValue();
    const dto: Partial<PaymentType> = {
      id: this.editing() ? Number(v.id) : 0,
      category: v.category,
      typeCode: (v.typeCode || '').trim().toUpperCase(),
      typeName: (v.typeName || '').trim(),
      displayOrder: Number(v.displayOrder) || 0,
      isActive: !!v.isActive,
    };

    this.saving.set(true);
    this.svc.ptSave(dto).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save lookup.');
          return;
        }
        this.toast.success(res?.message || (this.editing() ? 'Lookup updated' : 'Lookup created'));
        this.activeCategory.set(dto.category as PaymentCategory);
        this.closeModal();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  toggleActive(r: PaymentType) {
    const next = !r.isActive;
    this.patchRow(r.id, { isActive: next });
    this.svc.ptSetActive(r.id, next).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.patchRow(r.id, { isActive: r.isActive });
          this.toast.error(res?.message || 'Could not update status.');
        }
      },
      error: () => {
        this.patchRow(r.id, { isActive: r.isActive });
        this.toast.error('Could not update status.');
      },
    });
  }

  private patchRow(id: number, patch: Partial<PaymentType>) {
    this.rows.update((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  async remove(r: PaymentType) {
    const ok = await this.toast.confirm(
      `${r.typeName} (${r.typeCode}) will be deleted from ${this.categoryLabels[r.category] || r.category}.`,
      'Delete lookup?',
      'Delete',
    );
    if (!ok) return;
    this.svc.ptDelete(r.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete.');
          return;
        }
        this.toast.success(res?.message || 'Lookup deleted');
        this.load();
      },
    });
  }
}
